import React, { useState, useEffect, useRef } from 'react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useGameCategories } from '../hooks/useGameCategories';
import { getIcon } from '../utils/iconHelper';
import '../styles/CollectionFilters.css';

import { getApiUrl } from "../utils/config";
const API_URL = getApiUrl();

const CollectionFilters = ({ selectedCollection, onCollectionChange, selectedSubcollection, onSubcollectionChange }) => {
    const [showSubcollections, setShowSubcollections] = useState(false);
    const { t } = useTranslation();

    // Refs для прокрутки
    const mainScrollRef = useRef(null);
    const subScrollRef = useRef(null);

    // Получаем все категории из унифицированного API
    const { collections, subcollections, loading, error } = useGameCategories('filters');

    // Функция для получения иконки и названия категории из данных
    const getCategoryData = (collectionKey) => {
        // Сначала ищем в основных коллекциях
        const collection = collections.find(col => col.key === collectionKey);

        if (collection) {
            return {
                icon: getIcon(collection.icon),
                title: t(collection.titleKey, collection.key)
            };
        }

        // Потом ищем в подкатегориях
        for (const [parentKey, subCats] of Object.entries(subcollections)) {
            const subCollection = subCats.find(sub => sub.key === collectionKey);
            if (subCollection) {
                return {
                    icon: getIcon(subCollection.icon),
                    title: t(subCollection.titleKey, subCollection.key)
                };
            }
        }

        // Фоллбэк для неизвестных категорий
        return {
            icon: getIcon('dice'),
            title: collectionKey
        };
    };

    useEffect(() => {
        // Показываем подподборки если выбрана основная подборка и есть подподборки для неё
        setShowSubcollections(selectedCollection && subcollections[selectedCollection] && subcollections[selectedCollection].length > 0);
    }, [selectedCollection, subcollections]);

    const handleCollectionClick = (collectionKey) => {
        if (selectedCollection === collectionKey) {
            // Если кликнули на уже выбранную подборку, сбрасываем выбор
            onCollectionChange('');
            onSubcollectionChange('');
        } else {
            onCollectionChange(collectionKey);
            onSubcollectionChange(''); // Сбрасываем подподборку при смене основной
        }
    };

    const handleSubcollectionClick = (subcollectionKey) => {
        if (selectedSubcollection === subcollectionKey) {
            onSubcollectionChange('');
        } else {
            onSubcollectionChange(subcollectionKey);
        }
    };

    // Функции для прокрутки
    const scrollLeft = (scrollRef) => {
        if (scrollRef.current) {
            scrollRef.current.scrollBy({ left: -200, behavior: 'smooth' });
        }
    };

    const scrollRight = (scrollRef) => {
        if (scrollRef.current) {
            scrollRef.current.scrollBy({ left: 200, behavior: 'smooth' });
        }
    };

    // Убираем всю логику проверки прокрутки, так как стрелки всегда активны

    if (loading) {
        return (
            <div className="collection-filters loading">
                <div className="collection-filters-skeleton">
                    {[...Array(6)].map((_, index) => (
                        <div key={index} className="collection-filter-skeleton"></div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="collection-filters">
            {/* Основные подборки */}
            <div className="collection-filters-main">
                <div className="collection-filters-container">
                    <button
                        className="collection-scroll-btn left"
                        onClick={() => scrollLeft(mainScrollRef)}
                        aria-label="Прокрутить влево"
                    >
                        <FaChevronLeft />
                    </button>

                    <div className="collection-filters-scroll" ref={mainScrollRef}>
                        <button
                            className={`collection-filter-btn ${!selectedCollection ? 'active' : ''}`}
                            onClick={() => handleCollectionClick('')}
                        >
                            <span className="collection-filter-icon">{getIcon('dice')}</span>
                            <span className="collection-filter-title">{t('categories.all_games', 'Все игры')}</span>
                        </button>
                        {collections.map(collection => {
                            const categoryData = getCategoryData(collection.key);
                            return (
                                <button
                                    key={collection.key}
                                    className={`collection-filter-btn ${selectedCollection === collection.key ? 'active' : ''}`}
                                    onClick={() => handleCollectionClick(collection.key)}
                                >
                                    <span className="collection-filter-icon">{categoryData.icon}</span>
                                    <span className="collection-filter-title">{categoryData.title}</span>
                                </button>
                            );
                        })}
                    </div>

                    <button
                        className="collection-scroll-btn right"
                        onClick={() => scrollRight(mainScrollRef)}
                        aria-label="Прокрутить вправо"
                    >
                        <FaChevronRight />
                    </button>
                </div>
            </div>

            {/* Подподборки */}
            {showSubcollections && selectedCollection && subcollections[selectedCollection] && (
                <div className="collection-filters-sub">
                    <div className="collection-filters-scroll" ref={subScrollRef}>
                        {subcollections[selectedCollection].map(subcollection => {
                            const categoryData = getCategoryData(subcollection.key);
                            return (
                                <button
                                    key={subcollection.key}
                                    className={`collection-filter-btn sub ${selectedSubcollection === subcollection.key ? 'active' : ''}`}
                                    onClick={() => handleSubcollectionClick(subcollection.key)}
                                >
                                    <span className="collection-filter-icon">{categoryData.icon}</span>
                                    <span className="collection-filter-title">{categoryData.title}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CollectionFilters;
